import React, { useContext, useEffect, useRef, useState } from 'react';
import postal from 'postal';
import TableEditContext from './tableEditContext';
import useEditableTableNavigation from '../useEditableTableNavigation';
import _ from 'lodash';
import { ApplicableTableSettings } from '../../../tableSettings';
import useDeepCompareEffect from '../../../hooks/useDeepCompareEffect';
import useGlobalYesNoDialog from '../../../hooks/useGlobalYesNoDialog';
import { isEveryPropertyEmpty } from '../../../util';

export type TableEditControlsHandle = {
  setTableReference: (tableReference: HTMLDivElement) => void,
  add: () => void,
};

type TableEditControlsProps = {
  tableOptions: ApplicableTableSettings,
}

const TableEditControlsImpl: React.ForwardRefRenderFunction<TableEditControlsHandle, TableEditControlsProps> = (props, ref) => {
  const { tableOptions } = props;
  const tableIdRef = useRef('');
  const focusColumnRef = useRef(-1);

  let enterSubscription: ISubscriptionDefinition<any> | null = null;

  function subscribeChannels() {
    enterSubscription = postal.subscribe({
      channel: 'ifms',
      topic: 'ifms.editable-table-onEnter',
      callback: (data = { backFocus: false, changeFocus: true, element:HTMLElement }) => {
        onEnter(data.backFocus, data.changeFocus, data.element);
      },
    });    
  }

  function unsubscribeChannels() {
    if (enterSubscription) {
      postal.unsubscribe(enterSubscription);
    }
  }
  const [tableId, _setTableId] = useState('');
  const [tick, setTick] = useState(0); // To be able to rerender based on observer changes

  const observer = useRef<MutationObserver | null>(null);
  const tableContainerRef = useRef<HTMLDivElement>();

  const tableEditContext = useContext(TableEditContext);
  //const [tempEditColumnIndex, setTempEditColumnIndex] = useState(tableEditContext.editColumnIndex); //Edit Mode일 때의 Edit Col Index
  const tableDataLength = tableEditContext.data.length;
  const tableParentDivId = tableEditContext.tableParentDivId;
  const { clearTableSelection } = useEditableTableNavigation(tableOptions.tableName, tableEditContext, onDelete, add, tableContainerRef.current, tableOptions.useVirtual);
  const [showGlobalYesNoDialog] = useGlobalYesNoDialog();
    
  function setTableId(id: string) {
    tableIdRef.current = id;
    _setTableId(id);
  }

  React.useImperativeHandle(ref, () => ({
    setTableReference,
    add,
  }));

  useEffect(() => {
    if (tableEditContext.initializeFocusedCell && tableDataLength > 0) {
      setTimeout(() => {
        setSelectedCol(1);
        setSelectedRow(0);
      }, 10);
    }
  }, [tableEditContext.initializeFocusedCell, tableDataLength])

  useDeepCompareEffect(() => {
    initialize();
    return uninitialize;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableParentDivId, tableEditContext, tableId]);

  function setSelectedCol(c: number) {
    tableEditContext.setEditColumnIndex(c);
  }

  function setSelectedRow(c: number) {
    tableEditContext.setEditRowIndex(c);
  }

  function initialize() {
    subscribeChannels();
  }

  function uninitialize() {
    unsubscribeChannels();
    uninitializeObserver();
  }

  function initializeObserver(targetNode: HTMLDivElement) {
    if (!targetNode) {
      return;
    }

    if (observer && observer.current) {
      uninitializeObserver();
    }

    const observerConfig = {
      attributes: true,
      childList: true,
      subtree: true,
    };

    observer.current = new MutationObserver(observe);
    observer.current.observe(targetNode, observerConfig);

    // Run initially for getting initial values
    observe();
  }

  function uninitializeObserver() {
    if (observer && observer.current) {
      observer.current.disconnect();
    }
  }

  function isScrolledIntoView(container: HTMLElement, row: HTMLElement) {
    const trect = container.getBoundingClientRect();
    const rect = row.getBoundingClientRect();

    const viewStart = trect.top;
    const viewEnd = trect.top + trect.height;

    return rect.top - 20 >= viewStart && (rect.top + rect.height) <= viewEnd; //20 = header height
  }

  function makeSureControlsAreScrolledIntoView() {
    if (!tableContainerRef.current) {
      return;
    }

    const tableNode = tableContainerRef.current.querySelector('div[role="table"]') as HTMLElement;
    if (!tableNode) {
      return;
    }

    const controlsInTable = tableContainerRef.current.querySelector('.data-row-selected')?.parentElement as HTMLElement;
    if (!controlsInTable) {
      return;
    }

    if (!isScrolledIntoView(tableNode, controlsInTable)) {
      controlsInTable.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }

  function observe() {
    if (!tableContainerRef.current) {
      return;
    }

    const tableNode = tableContainerRef.current.querySelector('div[role="table"]');
    if (tableNode && tableNode.id && tableNode.id !== tableId) {
      setTableId(tableNode.id);
      setTick(tick + 1);
    }
  }

  function setTableReference(container: HTMLDivElement) {
    if (container) {
      tableContainerRef.current = container;
      initializeObserver(container);
    }
  }

  async function onDelete(editRowIndex: number) {
    const curEditRowIndex = tableEditContext.editRowIndex;
    const tempEditRowIndex = typeof editRowIndex === 'number' ? editRowIndex : curEditRowIndex
    const canBeDeleted = tableEditContext.canDeleteRow ? tableEditContext.canDeleteRow(tableEditContext.editRowIndex) : true;
    if (tempEditRowIndex < 0 || !tableEditContext.enabled || !canBeDeleted) {
      return; // Should never happen
    }

    const editablePropertyNames = tableOptions.rowRenderers.filter((x: any) => !!x.EditCell).map((x: any) => x.accessor);

    const shouldConfirmDelete = (rowIndex: number): boolean => {
      if (tableEditContext.shouldConfirmBeforeDelete) {
        return tableEditContext.shouldConfirmBeforeDelete(rowIndex);
      }
      
      return !isEveryPropertyEmpty(
        _.pick(tableEditContext.data[rowIndex], editablePropertyNames)
      );
    };
    
    const showDeleteConfirmDialog = async () => {
      return await showGlobalYesNoDialog(
        <div>
          <h3>Confirm to Delete?</h3>
          Do you want to delete line item?
        </div>
      );
    };

    if (shouldConfirmDelete(tempEditRowIndex)) {
      const confirmed = await showDeleteConfirmDialog();
      if (!confirmed) {
        return;
      }
    }

    const deleted = await tableEditContext.deleteLine(tempEditRowIndex);

    if (deleted) {
      if (tableDataLength === 1) {
        add(0, tableEditContext.editColumnIndex);
      }
      else if (curEditRowIndex === tableDataLength - 1)
        setSelectedRow(curEditRowIndex - 1)
    }

    focusColumnRef.current = -1;
  }

  function onEnter(backFocus: boolean, changeFocus:boolean, element:HTMLElement) {
    if(!tableContainerRef.current?.contains(element)) return;

    tableEditContext.setTempEditColumnIndex(-1);
    const curEditColIndex = tableEditContext.editColumnIndex;
    if (curEditColIndex === -1) return;

    const lastIndex = tableEditContext.tableColumnLength - 1;

    const shouldMoveColumn = changeFocus && ((backFocus && curEditColIndex !== 0) || (!backFocus && curEditColIndex !== lastIndex));

    if (shouldMoveColumn) {
      const newIndex = backFocus ? curEditColIndex - 1 : curEditColIndex + 1;
      setSelectedCol(newIndex);
    }
    else {
      const cellId = `${tableId}-cell-${tableEditContext.editRowIndex}-${curEditColIndex}`;
      const cell = document.getElementById(cellId);

      if (cell) {
        cell.classList.add('data-cell-selected');
      }
    }

    document.getElementById(tableId)?.focus();
    return true;
  }

  function getFirsFocusableColumnIndex(rowIndex: number): number {
    const tableNode = tableContainerRef.current as HTMLDivElement;
    let firstSelectableColumnIndex = 0;

    for (; firstSelectableColumnIndex < tableEditContext.tableColumnLength; firstSelectableColumnIndex++) {
      const editCellClass = `.ifms-editable-cell-${rowIndex}-${firstSelectableColumnIndex}`;
      const editCell = tableNode.querySelector<HTMLDivElement>(editCellClass);

      if (!editCell) {
        continue;
      }
      const hasDisabledChild = Array.from(editCell.children).some(child => 
        child.classList.contains('table-cell-disabled')
      );
      if (!hasDisabledChild) {
        break;
      }
    }

    return firstSelectableColumnIndex;
  }

  function add(focusRowIndex?: number, focusColIndex?: number) {
    if (!tableEditContext.enabled || (tableEditContext.canAdd !== undefined && !tableEditContext.canAdd)) return;
    let added = false;

    const result = tableEditContext.addLine();
    if (result) {
      tableEditContext.setTempEditColumnIndex(-1);
      const { setDataFunc, newData, curListData } = result;
      let tempCurListData = _.cloneDeep(curListData);

      if (focusRowIndex !== undefined) {
          tempCurListData.splice(focusRowIndex, 0, newData);
      } else {
          tempCurListData.push(newData);
      }
      setDataFunc(tempCurListData, focusRowIndex);
      added = true;

      if (added) {
        let rowCount = 0;
        if (typeof added === 'boolean') rowCount = tableEditContext.rowCount - 1;

        clearTableSelection();
        setSelectedRow(focusRowIndex === undefined ? rowCount : focusRowIndex);
        setSelectedCol(focusColIndex === undefined ? getFirsFocusableColumnIndex(focusRowIndex === undefined ? rowCount : focusRowIndex) : focusColIndex);

        setTimeout(makeSureControlsAreScrolledIntoView, 200);
      }
    }

    return added;
  }

  function renderControlsBelowEditRow() {
    return (
      <></>
    );
  }

  return renderControlsBelowEditRow();
};

const TableEditControls = React.forwardRef(TableEditControlsImpl);

export default React.memo(TableEditControls);